import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

export interface UseStickToBottomOptions {
  viewportRef: RefObject<HTMLDivElement | null>;
  innerRef: RefObject<HTMLDivElement | null>;
  /** Distance from bottom (px) considered "at bottom". Default 80. */
  threshold?: number;
}

/**
 * Auto-scroll the viewport to its bottom as content grows — but only when the
 * user was already at (or near) the bottom. If they scrolled up to read
 * history, leave them be (ChatGPT pattern).
 *
 * Returns whether the user is currently at the bottom so callers can render
 * a manual "scroll to bottom" affordance.
 */
export function useStickToBottom(opts: UseStickToBottomOptions): { isAtBottom: boolean } {
  const { viewportRef, innerRef, threshold = 80 } = opts;

  // Stays true until the user actively scrolls up. Content growth must NOT
  // flip it (the new content was below the fold from the engine's POV — the
  // user was at the bottom *before* the growth, which is what matters).
  const isAtBottomRef = useRef(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  // Mark our own scrollTop writes so the scroll listener can ignore them and
  // preserve the user-vs-engine distinction.
  const programmaticScrollRef = useRef(false);

  useEffect(() => {
    const viewport = viewportRef.current;
    const inner = innerRef.current;
    if (!viewport || !inner) return;
    if (typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      if (!isAtBottomRef.current) return;
      // User was at the bottom before this growth — keep them there.
      // Explicitly clamp: browsers do this on assignment, but writing the
      // max value directly keeps the value readable in DOM-stubbed tests
      // and matches what we actually intend.
      programmaticScrollRef.current = true;
      const max = Math.max(0, viewport.scrollHeight - viewport.clientHeight);
      viewport.scrollTop = max;
    });
    ro.observe(inner);

    const onScroll = () => {
      // Ignore the scroll event triggered by our own scrollTop write.
      if (programmaticScrollRef.current) {
        programmaticScrollRef.current = false;
        return;
      }
      const dist = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      const atBottom = dist <= threshold;
      isAtBottomRef.current = atBottom;
      setIsAtBottom(atBottom);
    };
    viewport.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      ro.disconnect();
      viewport.removeEventListener('scroll', onScroll);
    };
  }, [viewportRef, innerRef, threshold]);

  return { isAtBottom };
}
